// Account-scoped Site snapshots and immutable save attempts. IndexedDB commits
// finish before the UI may say "Saved on device". Web Locks serialize tabs.
import { createOfflineHistoryBridge } from './offlineHistoryBridge.js';
import { historyAttachment, historyKey } from './editorHistory.js';
import { captureHistorySave, acceptHistorySave, drainHistoryQueue, commitHistoryUploadProgress } from './offlineHistoryTransport.js';
import { readHistoryCleanupReferences, requestHistoryCleanupReferences } from './historyCleanupReferences.js';
import { readStorage } from './storageKeys.js';
import { createHistoryImageContextRegistry } from './editorHistoryImageContext.js';
import { validateReferenceUploadCapabilities, validateReferenceUploadReceipt } from './referenceUpload.js';
import { referenceMetadataKey, referenceBlobKey, referenceCapabilityKey, referenceMetadata, summarizeReferences, validReferenceDescriptor, referenceAttempt, prepareDurableReference, commitDurableReference, referenceIDs } from './offlineReferenceAssets.js';
import { retainedDrawingToken, selectedRetainedDrawing, verifiedRetainedBytes, retainedDrawingReferences, retainedDrawingReason, retainedDrawingMetadata } from './referenceUploadRecovery.js';
const DB = 'avisera-offline-sites-v1';
let active = null, transport, database, accountGeneration = 0;
const unavailable = e => e.status === 0 || e.status >= 500;
const fail = (message, status = 0) => Object.assign(new Error(message), { status });
const accountKey = user => `${user.tenant?.id || ''}:${user.id}`;
const clone = value => structuredClone(value);
const channel = typeof window !== 'undefined' && typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('avisera-offline-sites') : null;
const notify = () => { window.dispatchEvent(new Event('avisera-offline-change')); channel?.postMessage('changed'); };
// Account invalidation must reach open tabs even if clearing IndexedDB fails.
function notifyReferenceAccount() {
  const message = { action: 'avisera-reference-account-changed', account: active?.key || null };
  try { navigator.serviceWorker?.controller?.postMessage(message); } catch {}
  navigator.serviceWorker?.getRegistrations?.().then(registrations => {
    for (const registration of registrations) for (const worker of [registration.active, registration.waiting, registration.installing]) worker?.postMessage(message);
  }).catch(() => {});
}
const historyImageContexts = createHistoryImageContextRegistry({ account: () => active?.key || null, pathname: () => globalThis.location?.pathname || '', changed: () => { try { navigator.serviceWorker?.controller?.postMessage({ action: 'avisera-history-context-changed' }); } catch {} } });
// Internal opt-in only. No editor currently registers persistent history.
export const registerHistoryImageContext = (identity, assertCurrent) => historyImageContexts.register(identity, assertCurrent);
const notifyAccount = () => { accountGeneration++; historyImageContexts.invalidate(); notifyReferenceAccount(); window.dispatchEvent(new Event('avisera-offline-change')); channel?.postMessage({ type: 'account', key: active?.key || null }); };
if (channel) channel.onmessage = ({ data }) => {
  if (data?.type === 'account') {
    accountGeneration++; historyImageContexts.invalidate();
    if (data.key !== (active?.key || null)) active = null;
    notifyReferenceAccount();
  }
  window.dispatchEvent(new Event('avisera-offline-change'));
};
export const currentOfflineAccountKey = () => active?.key || null;

function db() {
  if (!database) database = new Promise((resolve, reject) => {
    const open = indexedDB.open(DB, 1);
    open.onupgradeneeded = () => { open.result.createObjectStore('sites', { keyPath: 'key' }); open.result.createObjectStore('meta'); };
    open.onsuccess = () => resolve(open.result); open.onerror = () => reject(open.error);
  }).catch(e => { database = null; throw e; });
  return database;
}
async function access(store, operation, write = false) {
  const database = await db();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(store, write ? 'readwrite' : 'readonly'), request = operation(tx.objectStore(store));
    tx.oncomplete = () => resolve(request.result);
    tx.onerror = tx.onabort = () => reject(tx.error || request.error || new Error('Device storage is unavailable.'));
  });
}
const get = key => access('sites', s => s.get(key));
const put = value => access('sites', s => s.put(value), true);
const meta = () => access('meta', s => s.get('account'));
const setMeta = value => access('meta', s => s.put(value, 'account'), true);
const keyFor = (id, account = active) => `${account.key}/${id}`;
const storageLock = account => 'avisera-storage:' + account.key;
const editorLock = account => 'avisera-editors:' + account.key;
const lock = (key, fn) => navigator.locks.request('avisera-storage:' + key.split('/')[0], { mode: 'shared' }, () => navigator.locks.request('avisera-site:' + key, fn));
const owned = async account => {
  const saved = await meta();
  if (!account || saved?.key !== account.key || active?.key !== account.key) throw fail('Sign in to the account that owns these saved edits.', 401);
};
function reportDeviceSite(record) {
  const native = window.webkit?.messageHandlers?.aviseraNative;
  try { if (native && window.aviseraNativeCapabilities?.accountID === active?.user.id) native.postMessage({ id: crypto.randomUUID(), action: 'offlineSiteReady', siteID: record.project.id, name: record.project.name,
    ...(record.createdOnDevice ? { createdOnDevice: true, pendingCreation: !!record.creation, pendingUpload: !!(record.creation || record.pending.length), rev: record.project.rev, updatedAt: record.project.updatedAt } : {}) }); } catch { /* Native labels are metadata; the durable Site remains in IndexedDB. */ }
}
export async function offlineCreationReady() {
  return !!(active && navigator.serviceWorker?.controller && await access('meta', s => s.get('editor:' + active.key)));
}
async function createDeviceSite(body, send, account) {
  await owned(account);
  if (typeof body.name !== 'string' || !body.name.trim() || new TextEncoder().encode(body.name.trim()).length > 120) throw fail('That Site name is too long or empty. Use a shorter name.', 400);
  const creationId = crypto.randomUUID();
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${account.key}/${creationId}`));
  const id = 'offline_' + Array.from(new Uint8Array(digest).slice(0,16), b => b.toString(16).padStart(2,'0')).join('');
  const key = keyFor(id), now = new Date().toISOString();
  const record = { key, account: account.key, project: { id, name: body.name, description: '', rev: 0, archived: false, createdAt: now, updatedAt: now },
    doc: clone(body.doc), creation: { ...clone(body), creationId, expectedAccount: account.key }, createdOnDevice: true, generation: 0, pending: [], ready: true, savedAt: now };
  return lock(key, async () => {
    await owned(account); await put(record); reportDeviceSite(record); notify();
    const acknowledged = clone(record);
    // Creation is acknowledged once its local transaction commits. A later
    // upload/checkpoint failure must not invite creating a duplicate local Site.
    const saved = await drain(record, send, account).catch(() => acknowledged);
    return { project: saved.project, doc: saved.doc, offlineVersion: saved.generation, offline: !!saved.creation };
  });
}
export async function offlineSite(id) { return active ? get(keyFor(id)) : null; }
export async function offlineReferenceAvailability(projectID) {
  const account = active;
  if (!account) return { available: false, prepared: false, reason: 'Sign in and prepare this Site for offline work.' };
  try {
    await owned(account);
    const record = await get(keyFor(projectID, account));
    if (!record?.ready) return { available: false, prepared: false, reason: 'Prepare this Site for offline work first.' };
    if (!navigator.locks) throw new Error('This browser cannot safely coordinate saved device edits. Open this prepared Site in an updated Avisera app or a browser with offline storage support.');
    if (record.revoked) throw new Error('Reconnect and sign in to reopen this Site before importing drawings.');
    const worker = navigator.serviceWorker?.controller;
    if (!worker || (await workerMessage(worker, { action: 'editorStorage' }, undefined, 10000)).localReferenceUploads !== 1) throw new Error('Connect and update the offline editor before saving new drawings on this device.');
    const capability = await access('meta', s => s.get(referenceCapabilityKey(account.key)));
    validateReferenceUploadCapabilities(capability, { size: 0 });
    await owned(account);
    return { available: true, prepared: true, reason: '' };
  } catch (error) { return { available: false, prepared: true, reason: error.message }; }
}
async function historySession(account, caller = () => {}) {
  const generation = accountGeneration, worker = navigator.serviceWorker?.controller;
  const check = () => {
    if (active?.key !== account?.key || generation !== accountGeneration || !worker || navigator.serviceWorker?.controller !== worker) throw fail('The account session or offline editor changed. Reopen this Site before retaining history.', 409);
    const value = caller(); if (value === false || value?.then) throw fail('The reviewed history context changed. Reopen the Site before saving.', 409);
  };
  check(); await owned(account); check();
  const capability = await workerMessage(worker, { action: 'editorStorage' }, undefined, 10000);
  if (!navigator.locks || capability?.historyImageFallback !== 1 || capability?.savedEditHistory !== 1) throw fail('Update the offline editor before retaining edit history.', 409);
  check(); return check;
}
// Guarded internal APIs for a future history UI; no editor invokes these yet.
const offlineHistoryBridge = createOfflineHistoryBridge({
  account: () => active, generation: () => accountGeneration,
  controller: () => navigator.serviceWorker?.controller,
  pathname: () => globalThis.location?.pathname || '',
  database: db, owned, workerMessage, locks: () => navigator.locks,
  registerContext: (identity, guard) => historyImageContexts.register(identity, guard),
  drafts: browserDrafts, sameDrafts,
  hasSiteDraft: (account, id) => readStorage(`draft.${account.user.id}.${id}`) != null,
  notify,
});
export const offlineHistoryAvailability = (id, options) => offlineHistoryBridge.availability(id, options);
export const retainedOfflineEditorHistories = options => offlineHistoryBridge.list(options);
export const exportOfflineEditorHistory = (selection, options) => offlineHistoryBridge.export(selection, options);
export const discardOfflineEditorHistory = (selection, options) => offlineHistoryBridge.discard(selection, options);
export const resetOfflineEditorHistory = (selection, options) => offlineHistoryBridge.reset(selection, options);
export const attachOfflineEditorHistory = (id, options) => offlineHistoryBridge.attach(id, options);

async function referenceSummary(account) { return summarizeReferences(await referenceMetadata(await db(), account.key), account.key); }
async function referenceRemovalReason(id, account) {
  return (await referenceSummary(account)).bySite[id]?.pending ? 'Upload or resolve pending drawings before removing this Site download.' : '';
}

async function records(account = active) { return account ? (await access('sites', s => s.getAll())).filter(s => s.account === account.key) : []; }

// Hold a shared lease for the lifetime of an editor, including unsaved changes.
// Storage removal takes an exclusive lease and never waits for an open editor.
export function retainOfflineEditor() {
  const account = active;
  if (!account || !navigator.locks) return () => {};
  const controller = new AbortController(); let release;
  const finished = new Promise(resolve => { release = resolve; });
  navigator.locks.request(editorLock(account), { mode: 'shared', signal: controller.signal }, () => finished).catch(() => {});
  return () => { controller.abort(); release(); };
}
function removalReason(record, account) {
  if (record.creation || record.pending.length) return 'Upload pending changes before removing this download.';
  if (record.recovered || record.recoveryHistory?.length) return 'A recovery backup is kept here. Review it in Manage backups before removing this download.';
  if (readStorage(`draft.${account.user.id}.${record.project.id}`) != null) return 'Open this Site and save or discard its recovery draft first.';
  return '';
}
export async function offlineStorageSummary() {
  const account = active; await owned(account);
  const saved = await records(account);
  const estimate = await navigator.storage?.estimate?.().catch(() => null);
  let editor = { error: 'Prepare or update the offline editor to manage older downloads.' };
  const worker = navigator.serviceWorker?.controller;
  if (worker) try { editor = await workerMessage(worker, { action: 'editorStorage' }, undefined, 10000); }
  catch { editor = { error: 'Update offline editor while connected to enable editor cleanup.' }; }
  const photos = await (await import('./fieldPhotos.js')).fieldPhotoStorageSummary();
  const references = await referenceSummary(account);
  await owned(account);
  return { fieldPhotos: photos, references, usage: estimate?.usage, quota: estimate?.quota, editor, sites: saved.map(record => ({
    id: record.project.id, name: record.project.name, savedAt: record.savedAt,
    bytes: new TextEncoder().encode(JSON.stringify(record)).length,
    pending: record.pending.length + (record.creation ? 1 : 0), blocked: !!record.blocked,
    backups: (record.recovered ? 1 : 0) + (record.recoveryHistory?.length || 0),
    referenceBytes: references.bySite[record.project.id]?.bytes || 0,
    pendingReferences: references.bySite[record.project.id]?.pending || 0,
    photoBytes: photos.bySite[record.project.id]?.bytes || 0,
    pendingPhotos: photos.bySite[record.project.id]?.pending || 0,
    reason: removalReason(record, account) || (references.bySite[record.project.id]?.pending ? 'Upload or resolve pending drawings before removing this Site download.' : '') || (photos.bySite[record.project.id]?.pending ? 'Upload or resolve pending private photos and removals before removing this Site download.' : ''),
  })).sort((a, b) => a.name.localeCompare(b.name)) };
}
export async function cleanupOfflineEditor() {
  const account = active; await owned(account);
  const worker = navigator.serviceWorker?.controller;
  if (!worker) throw new Error('Update offline editor before cleanup.');
  try { return await workerMessage(worker, { action: 'cleanupEditor', account: account.key }, undefined, 60000); }
  finally { notify(); }
}
function browserDrafts(errorMessage = 'A browser recovery draft could not be read. Images were kept to protect its contents.') {
  try {
    const drafts = new Map();
    for (let i = 0; i < localStorage.length; i++) {
      const name = localStorage.key(i);
      if (name?.includes('.draft.')) {
        const value = localStorage.getItem(name), doc = JSON.parse(value);
        if (!doc || typeof doc !== 'object' || Array.isArray(doc) || ('doc' in doc && (!doc.doc || typeof doc.doc !== 'object' || Array.isArray(doc.doc)))) throw new Error('Invalid draft');
        drafts.set(name, { value, doc });
      }
    }
    return drafts;
  } catch { throw new Error(errorMessage); }
}
function sameDrafts(before) {
  const current = browserDrafts();
  if (current.size !== before.size || [...current].some(([name, draft]) => before.get(name)?.value !== draft.value)) throw new Error('A browser recovery draft changed. Try removing unused images again.');
}
function cleanupImageReferences(object, ids = new Set()) {
  if (object && typeof object === 'object') for (const [key, value] of Object.entries(object)) {
    if (['imageId', 'photoId', 'testImageId'].includes(key)) {
      if (value == null || value === '') continue;
      // Never infer an orphan from an ambiguous reference. URL parsing or the
      // server may normalize it differently from an exact cache-key comparison.
      if (typeof value !== 'string' || !/^[a-zA-Z0-9_-]{1,100}$/.test(value)) throw new Error('A saved Site or browser draft has an unreadable image reference. Images were kept to protect its contents.');
      ids.add(value);
    } else if (typeof value === 'object') cleanupImageReferences(value, ids);
  }
  return ids;
}
function cleanupHistoryGuard(account) {
  const generation = accountGeneration;
  return () => { if (!account || active?.key !== account.key || generation !== accountGeneration) throw fail('The owning account session changed. Reopen Offline storage before cleanup.', 401); };
}
const mergeHistoryReferences = (retained, history) => { for (const id of history.references.keys()) retained.add(id); return retained; };
async function removeUnusedReferenceAssets(account, retained, projectID, assertSafe, assertCurrent) {
  const entries = await referenceMetadata(await db(), account.key);
  summarizeReferences(entries, account.key); // Fail closed on unreadable metadata.
  let removed = 0, kept = 0; const removedIDs = new Set(), retainedIDs = new Set();
  for (const { key, value } of entries) {
    if (projectID && value.projectID !== projectID) continue;
    if (value.state !== 'uploaded' || retained.has(value.imageID)) { kept++; retainedIDs.add(value.imageID); continue; }
    await assertSafe();
    const database = await db();
    const protectedByHistory = await new Promise((resolve, reject) => {
      const tx = database.transaction('meta', 'readwrite'), store = tx.objectStore('meta');let owner, current, history, remaining = 3, failure, protectedImage = false;
      const abort = error => { failure = error; try { tx.abort(); } catch {} };
      const finish = () => { if (--remaining) return; try {
        assertCurrent(); if (owner?.key !== account.key) throw fail('The owning account changed.', 401);
        if (JSON.stringify(current) !== JSON.stringify(value)) throw new Error('A drawing upload changed during cleanup. Its original was kept.');
        protectedImage = history.references.has(value.imageID);
        if (!protectedImage) { store.delete(key); store.delete(referenceBlobKey(account.key, value.imageID)); }
        assertCurrent();
      } catch (error) { abort(error); } };
      const a=store.get('account'),b=store.get(key);a.onsuccess=()=>{owner=a.result;finish();};b.onsuccess=()=>{current=b.result;finish();};
      requestHistoryCleanupReferences(store, account.key, value=>{history=value;finish();}, abort);
      tx.oncomplete = () => resolve(protectedImage); tx.onerror = tx.onabort = () => reject(failure || tx.error || new Error('Drawing cleanup could not finish.'));
    });
    if (protectedByHistory) { kept++; retainedIDs.add(value.imageID); }
    else { removed++; removedIDs.add(value.imageID); }
  }
  return { removed, retained: kept, removedIDs, retainedIDs };
}
export async function cleanupOfflineImages() {
  const account = active, assertCurrent = cleanupHistoryGuard(account); await owned(account); assertCurrent();
  if (!navigator.locks || !globalThis.caches) throw new Error('This browser cannot safely coordinate image cleanup. Images were kept.');
  try {
    return await navigator.locks.request(editorLock(account), { ifAvailable: true }, async lease => {
      if (!lease) throw new Error('Close open Site editors on this device before removing unused images.');
      return navigator.locks.request(storageLock(account), async () => {
        await owned(account);
        const worker = navigator.serviceWorker?.controller;
        if (!worker || !(await workerMessage(worker, { action: 'editorStorage' }, undefined, 10000)).imageCleanup) throw new Error('Update offline editor while connected before removing unused images.');
        // Preparation holds the storage lock, and worker writes keep this lease
        // even if their requesting page closes or its message times out.
        return navigator.locks.request('avisera-images:' + account.key, { ifAvailable: true }, async imageLease => {
          if (!imageLease) throw new Error('A Site image download is in progress. Wait for it to finish before cleanup.');
          const check = async () => {
            assertCurrent(); await owned(account); assertCurrent();
            if (navigator.serviceWorker.controller !== worker) throw new Error('The offline editor changed. Reopen Offline storage before removing unused images.');
          };
          await check();
          const saved = (await access('sites', store => store.getAll())).filter(record => record.account === account.key || record.key?.startsWith(account.key + '/'));
          const document = value => value && typeof value === 'object' && !Array.isArray(value);
          if (saved.some(record => record.account !== account.key || !document(record.doc) || !Array.isArray(record.pending) || record.pending.some(item => !document(item?.doc)) ||
            (record.creation && !document(record.creation.doc)) || (record.recovered && !document(record.recovered.doc)) ||
            (record.recoveryHistory != null && (!Array.isArray(record.recoveryHistory) || record.recoveryHistory.some(item => !document(item?.doc)))))) throw new Error('A saved Site could not be read completely. Images were kept to protect its contents.');
          const retained = mergeHistoryReferences(cleanupImageReferences(saved), await readHistoryCleanupReferences(await db(), account.key, assertCurrent)), drafts = browserDrafts();
          for (const { doc } of drafts.values()) cleanupImageReferences(doc, retained);
          await check();
          const name = 'avisera-private-images-' + encodeURIComponent(account.key);
          const cache = await caches.open(name), requests = await cache.keys();
          // Pending/unconfirmed imports are retained even when a later Undo no
          // longer references them: their immutable earlier save may still need
          // the original image. Explicit cleanup only removes uploaded orphans.
          const referenceCleanup = await removeUnusedReferenceAssets(account, retained, undefined, async () => { await check(); sameDrafts(drafts); }, assertCurrent);
          let removed = 0, kept = 0, ignored = 0;
          try {
            for (const request of requests) {
              const url = new URL(request.url), match = url.pathname.match(/^\/api\/images\/([a-zA-Z0-9_-]{1,100})$/);
              if (request.method !== 'GET' || url.origin !== location.origin || url.search || url.hash || !match) { ignored++; continue; }
              if (referenceCleanup.retainedIDs.has(match[1])) continue;
              if (retained.has(match[1])) { kept++; continue; }
              await check(); sameDrafts(drafts);
              const history = await readHistoryCleanupReferences(await db(), account.key, assertCurrent);
              if (history.references.has(match[1])) { kept++; continue; }
              if (await cache.delete(request) && !referenceCleanup.removedIDs.has(match[1])) removed++;
            }
          } catch (e) { throw new Error(`${removed} unused cached ${removed === 1 ? 'image removed' : 'images removed'} before cleanup stopped. ${e.message}`); }
          return { removed: removed + referenceCleanup.removed, retained: kept + referenceCleanup.retained, ignored };
        });
      });
    });
  } finally { notify(); }
}
export async function removeOfflineSite(id) {
  const account = active, assertCurrent = cleanupHistoryGuard(account); await owned(account); assertCurrent();
  return navigator.locks.request(editorLock(account), { ifAvailable: true }, async lease => {
    if (!lease) throw new Error('Close open Site editors on this device, then try removing the download again.');
    return navigator.locks.request(storageLock(account), async () => {
      await owned(account);
      const key = keyFor(id, account), record = await get(key);
      if (!record) return;
      const photos = await import('./fieldPhotos.js');
      const reason = removalReason(record, account) || await referenceRemovalReason(id, account) || await photos.fieldPhotoRemovalReason(id, account.key); if (reason) throw new Error(reason);
      // Retain images referenced by any remaining plan, queued save, backup or
      // browser draft. Parsing failure stops cleanup before anything is removed.
      const sourceImages = cleanupImageReferences(record);
      const retained = mergeHistoryReferences(cleanupImageReferences((await records(account)).filter(r => r.key !== key)), await readHistoryCleanupReferences(await db(), account.key, assertCurrent));
      for (const { doc } of browserDrafts('A browser recovery draft could not be read. Downloads were kept to protect its images.').values()) cleanupImageReferences(doc, retained);
      await owned(account); assertCurrent(); const databaseValue = await db();
      // Recheck history in the same transaction that removes the Site record.
      // Retained checkpoints/originals intentionally become orphaned, not deleted.
      await new Promise((resolve,reject)=>{let failure,owner,current,history,left=3;const tx=databaseValue.transaction(['sites','meta'],'readwrite'),siteStore=tx.objectStore('sites'),metaStore=tx.objectStore('meta');
        const abort=error=>{failure=error;try{tx.abort();}catch{}};
        const done=()=>{if(--left)return;try{assertCurrent();if(owner?.key!==account.key)throw fail('The owning account changed.',401);if(JSON.stringify(current)!==JSON.stringify(record))throw new Error('The Site changed before download removal.');mergeHistoryReferences(retained,history);siteStore.delete(key);assertCurrent();}catch(error){abort(error);}};
        const a=metaStore.get('account'),b=siteStore.get(key);a.onsuccess=()=>{owner=a.result;done();};b.onsuccess=()=>{current=b.result;done();};requestHistoryCleanupReferences(metaStore,account.key,value=>{history=value;done();},abort);
        tx.oncomplete=resolve;tx.onabort=tx.onerror=()=>reject(failure||tx.error||new Error('The Site download could not be removed.'));
      });
      try {
        if (window.aviseraNativeCapabilities?.accountID === account.user.id) window.webkit?.messageHandlers?.aviseraNative?.postMessage({ id: crypto.randomUUID(), action: 'offlineSiteRemoved', siteID: id });
      } catch { /* The device document has already been removed successfully. */ }
      notify();
      let warning = '';
      try {
        await navigator.locks.request('avisera-images:' + account.key, async () => {
          await removeUnusedReferenceAssets(account, retained, id, () => owned(account), assertCurrent);
        });
      } catch { warning = 'The Site download was removed. Some drawing downloads could not be cleared.'; }
      try { await photos.clearDownloadedFieldPhotos(id, account.key); }
      catch { warning = 'The Site download was removed. Some private photo downloads could not be cleared.'; }
      try {
        const name = 'avisera-private-images-' + encodeURIComponent(account.key);
        if (await caches.has(name)) {
          const cache = await caches.open(name);
          for (const imageID of sourceImages) if (!retained.has(imageID)) {
            const history = await readHistoryCleanupReferences(await db(), account.key, assertCurrent);
            if (!history.references.has(imageID)) await cache.delete('/api/images/' + imageID);
          }
        }
      } catch { warning = 'The Site download was removed. Some cached images could not be cleared.'; }
      return { warning };
    });
  });
}
export async function cachedAccount() {
  const value = await meta();
  const native = globalThis.window?.aviseraNativeCapabilities?.accountID;
  return value && (!native || value.user.id === native) ? value : null;
}

async function identify(send) {
  const result = await send('GET', '/api/auth/me', undefined, {});
  if (!active || accountKey(result.user) !== active.key) throw fail('Your account changed. Reopen this Site under its original account.', 401);
}
async function uploadRequiredReferences(record, document, send, account, historyGuard) {
  const guard = historyGuard || (() => {});
  const progress = async (key, previous, next) => historyGuard ? commitHistoryUploadProgress(await db(), { account: account.key, key, previous, next }, guard) : access('meta', s => s.put(next, key), true);
  guard();
  for (const imageID of referenceIDs(document)) {
    guard();
    const key = referenceMetadataKey(account.key, imageID);
    const descriptor = await access('meta', s => s.get(key));
    if (!descriptor) continue; // Existing server images keep ordinary validation.
    if (!validReferenceDescriptor(descriptor, account.key, imageID) || descriptor.projectID !== record.project.id) throw fail('A queued drawing belongs to another Site or has unreadable metadata.', 409);
    if (descriptor.state === 'uploaded') {
      validateReferenceUploadReceipt({ image: descriptor.receipt }, { ...descriptor, accountKey: account.key }); continue;
    }
    try {
      guard(); await owned(account); guard();
      const bytes = await access('meta', s => s.get(referenceBlobKey(account.key, imageID)));
      const attempt = referenceAttempt(descriptor, bytes);
      const capability = await send('GET', '/api/image-uploads/capabilities', undefined, {});
      validateReferenceUploadCapabilities(capability, attempt); await owned(account); guard();
      const result = await send('PUT', `/api/projects/${record.project.id}/images/${attempt.uploadID}`, attempt.bytes, { headers: attempt.headers });
      const receipt = validateReferenceUploadReceipt(result, attempt); await owned(account); guard();
      await progress(key, descriptor, { ...descriptor, state: 'uploaded', receipt, error: '', blockedStatus: null });
    } catch (error) {
      guard(); await owned(account); guard();
      if (!unavailable(error)) {
        await progress(key, descriptor, { ...descriptor, state: 'blocked', error: error.message, blockedStatus: error.status || 400 });
      }
      throw error;
    }
  }
}
async function drain(record, send, account) {
  const generation = accountGeneration, controller = navigator.serviceWorker?.controller;
  const sameSession = () => { if (generation !== accountGeneration || controller !== navigator.serviceWorker?.controller || active?.key !== account.key) throw fail('The account or offline editor changed during upload.', 409); };
  if ((!record.creation && !record.pending.length) || record.blocked || navigator.onLine === false) return record;
  let attachedHistory = false;
  if (Object.hasOwn(record, 'editorHistory')) {
    const checkpoint = await access('meta', s => s.get(historyKey(account.key, record.project.id)));
    sameSession(); attachedHistory = historyAttachment(checkpoint, record).state === 'active';
  }
  // Older/ordinary writers may detach a checkpoint. Preserve that evidence,
  // but do not make their ordinary upload depend on repairing edit history.
  if (attachedHistory) {
    const assertCurrent = await historySession(account, sameSession);
    return navigator.locks.request('avisera-images:' + account.key, async () => {
      await owned(account); assertCurrent(); await identify(send); assertCurrent();
      const result = await drainHistoryQueue(await db(), { account: account.key, projectID: record.project.id, send,
        assertOwned: () => owned(account), uploadRequired: (current, doc, guard) => uploadRequiredReferences(current, doc, send, account, guard) }, { assertCurrent });
      try { notify(); } catch {} return result;
    });
  }
  try {
    await owned(account); await identify(send);
    if (record.creation) {
      const receipt = await send('POST', '/api/projects/offline', record.creation, {});
      if (receipt.project.id !== record.project.id) throw fail('The server returned an unexpected Site identity. Your device copy is retained.', 409);
      record.project = receipt.project;
      if (!record.pending.length) record.doc = receipt.doc;
      delete record.creation; record.error = ''; record.savedAt = new Date().toISOString();
      await put(record); reportDeviceSite(record); notify();
    }
    while (record.pending.length) {
      await owned(account);
      const head = record.pending[0];
      await uploadRequiredReferences(record, head.doc, send, account);
      if (head.expectedRev == null) { head.expectedRev = record.project.rev; await put(record); }
      const receipt = await send('POST', `/api/projects/${record.project.id}/versions`, head, {});
      // A lost response retries this exact mutation ID/body, even after restart.
      record.pending.shift(); record.project.rev = receipt.rev; record.error = ''; record.savedAt = new Date().toISOString();
      await put(record); notify();
    }
  } catch (e) {
    record.error = unavailable(e) ? '' : e.message;
    record.blocked = !unavailable(e) ? e.status || 400 : null;
    await put(record); notify();
  }
  if (record.createdOnDevice) reportDeviceSite(record);
  return record;
}
export async function syncOfflineSites(options = {}) {
  const account = active;
  if (!account || !transport || !navigator.locks) return;
  for (const value of await records(account)) {
    await lock(value.key, async () => { await owned(account); const record = await get(value.key); if (record) await drain(record, transport, account); });
  }
  if (!options.skipFieldPhotos) {
    await owned(account);
    await (await import('./fieldPhotos.js')).syncFieldPhotos(undefined, { siteQueueDrained: true }).catch(() => {});
  }
}
export async function retryOfflineSite(id) {
  const account = active, key = keyFor(id);
  await lock(key, async () => { await owned(account); const record = await get(key); if (record) { record.blocked = null; await put(record); await drain(record, transport, account); } });
  await owned(account);
  await (await import('./fieldPhotos.js')).syncFieldPhotos(id, { siteQueueDrained: true }).catch(() => {});
  notify();
}
// Preserve queued work locally before deliberately opening the online version.
export async function useLatestOfflineSite(id) {
  const account = active, key = keyFor(id);
  return lock(key, async () => {
    await owned(account); await identify(transport);
    const record = await get(key), latest = await transport('GET', `/api/projects/${id}`, undefined, {});
    if (record.recovered) (record.recoveryHistory ||= []).push(record.recovered);
    record.recovered = { doc: record.doc, pending: record.pending, savedAt: new Date().toISOString() };
    Object.assign(record, latest, { pending: [], blocked: null, error: '', generation: record.generation + 1 });
    await put(record); notify(); return latest;
  });
}
export async function recoveredOfflineSite(id) { return (await offlineSite(id))?.recovered; }

const backupEntries = record => [...(record.recovered ? [{ slot: 'current', value: record.recovered }] : []), ...(record.recoveryHistory || []).map((value, index) => ({ slot: 'history-' + index, value })).reverse()];
async function backupToken(account, id, entry) {
  const bytes = new TextEncoder().encode(JSON.stringify([account.key, id, entry.slot, entry.value]));
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2, '0')).join('');
}
export async function offlineBackups(id) {
  const account = active; await owned(account);
  const record = await get(keyFor(id, account));
  const backups = await Promise.all(backupEntries(record || {}).map(async entry => ({
    slot: entry.slot, token: await backupToken(account, id, entry), savedAt: entry.value.savedAt,
    pending: entry.value.pending?.length || 0, bytes: new TextEncoder().encode(JSON.stringify(entry.value)).length,
  })));
  await owned(account); return backups;
}
async function selectedBackup(record, account, id, selection) {
  const entry = backupEntries(record || {}).find(entry => entry.slot === selection.slot);
  if (!entry || await backupToken(account, id, entry) !== selection.token) throw new Error('This backup changed in another window. Select it again from the backup list.');
  return entry;
}
export async function readOfflineBackup(id, selection) {
  const account = active; await owned(account);
  return lock(keyFor(id, account), async () => {
    await owned(account);
    const record = await get(keyFor(id, account));
    const entry = await selectedBackup(record, account, id, selection);
    await owned(account);
    return { name: record.project.name, ...clone(entry.value) };
  });
}
// Drawings for a recovery ZIP: this account's retained originals first, then
// its private image cache, and, when allowed, a read-only server copy.
async function readArchiveDrawings(account, archive, snapshot, { signal, assertCurrent, onProgress, online = false }) {
  const assets = [], missing = [];
  let totalBytes = snapshot.bytes.length;
  const cacheName = 'avisera-private-images-' + encodeURIComponent(account.key);
  const cache = globalThis.caches && await caches.has(cacheName) ? await caches.open(cacheName) : null;
  for (const [index, imageID] of snapshot.imageIDs.entries()) {
    await assertCurrent();
    try {
      const descriptor = await access('meta', store => store.get(referenceMetadataKey(account.key, imageID)));
      let blob, sha256;
      if (descriptor !== undefined) {
        // Do not fall back to a possibly stale cache when an original exists
        // but has corrupt metadata, ownership, receipt or checksum.
        await retainedDrawingToken(account.key, descriptor);
        if (descriptor.imageID !== imageID) throw new Error('Drawing identity mismatch');
        const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(account.key + '/' + descriptor.projectID + '/' + descriptor.uploadID));
        const expectedID = 'ref_' + Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2,'0')).join('').slice(0,32);
        if (expectedID !== imageID) throw new Error('Drawing account identity mismatch');
        blob = await access('meta', store => store.get(referenceBlobKey(account.key, imageID)));
        referenceAttempt(descriptor, blob); sha256 = descriptor.sha256;
      } else {
        // Online Site backups may read a drawing the server already holds when this device has no copy.
        let response = cache && await cache.match('/api/images/' + imageID, { ignoreSearch: false });
        if (!response && online && navigator.onLine !== false) {
          response = await fetch('/api/images/' + encodeURIComponent(imageID), { credentials: 'same-origin', cache: 'no-store', redirect: 'error', ...(signal ? { signal } : {}) });
          await assertCurrent();
        }
        const mime = response?.headers.get('Content-Type')?.split(';')[0].trim().toLowerCase();
        const declared = response?.headers.get('Content-Length');
        if (!response || response.status !== 200 || !archive.archiveImageType(mime) || declared !== null && (!/^\d+$/.test(declared) || Number(declared) <= 0 || Number(declared) > archive.RECOVERY_ARCHIVE_MAX_IMAGE_BYTES)) throw new Error('Cached drawing is unavailable');
        const reader = response.body?.getReader();
        if (!reader) throw new Error('Cached drawing bytes unavailable');
        const chunks = []; let size = 0;
        const cancelRead = () => { void reader.cancel().catch(() => {}); };
        signal?.addEventListener('abort', cancelRead, { once: true });
        try {
          while (true) {
            await assertCurrent(); const { done, value } = await reader.read(); await assertCurrent();
            if (done) break;
            size += value.byteLength;
            if (size > archive.RECOVERY_ARCHIVE_MAX_IMAGE_BYTES || totalBytes + size > archive.RECOVERY_ARCHIVE_MAX_BYTES) throw archive.archiveError('This backup and its drawings exceed the 64 MiB archive limit.', 'archive-limit');
            chunks.push(value);
          }
        } finally { signal?.removeEventListener('abort', cancelRead); await reader.cancel().catch(() => {}); }
        if (!size || declared !== null && Number(declared) !== size) throw new Error('Cached drawing size mismatch');
        blob = new Blob(chunks, { type: mime });
      }
      if (totalBytes + blob.size > archive.RECOVERY_ARCHIVE_MAX_BYTES) throw archive.archiveError('This backup and its drawings exceed the 64 MiB archive limit.', 'archive-limit');
      assets.push({ imageID, blob, ...(sha256 ? { sha256 } : {}) }); totalBytes += blob.size;
    } catch (error) {
      if (error.name === 'AbortError' || error.status === 401 || error.code === 'archive-limit') throw error;
      missing.push(imageID);
    }
    await assertCurrent();
    onProgress?.({ phase: 'reading', completed: index + 1, total: snapshot.imageIDs.length, bytes: totalBytes });
  }
  return { assets, missing };
}
/** Export one immutable selected backup using only this account's local
 * originals/cache. The shared image lease excludes destructive cleanup while
 * snapshot bytes are assembled; no transport, writes or queue replay occurs. */
export async function exportOfflineBackupArchive(id, selection, { signal, onProgress, photos, photoList } = {}) {
  const account = active, generation = accountGeneration;
  const archive = await import('./portableRecoveryArchive.js');
  const assertCurrent = async () => {
    archive.checkArchiveSignal(signal);
    await owned(account);
    if (generation !== accountGeneration) throw fail('Your account session changed. Reopen this backup before exporting.', 401);
    archive.checkArchiveSignal(signal);
  };
  await assertCurrent();
  if (!navigator.locks) throw new Error('Use an updated browser with coordinated offline storage to export drawings.');
  const lease = (name, mode, fn) => navigator.locks.request(name, { mode, ...(signal ? { signal } : {}) }, fn);
  return lease(storageLock(account), 'shared', () => lease('avisera-site:' + keyFor(id, account), 'exclusive', () => lease('avisera-images:' + account.key, 'shared', async () => {
    await assertCurrent();
    const record = await get(keyFor(id, account));
    if (record?.revoked) throw fail('Offline access to this Site was revoked. Its recovery backup is kept; export plan data separately if needed.', 403);
    if (!record || record.account !== account.key || record.key !== keyFor(id, account) || record.project?.id !== id) throw fail('Reopen this Site under its owning account before exporting drawings.', 403);
    const entry = await selectedBackup(record, account, id, selection);
    const value = clone(entry.value), payload = { doc: value.doc, recovery: { name: record.project.name, savedAt: value.savedAt, pending: value.pending || [] } };
    const snapshot = archive.recoveryArchiveSnapshot(payload);
    const { assets, missing } = await readArchiveDrawings(account, archive, snapshot, { signal, assertCurrent, onProgress });
    if (missing.length) throw archive.unavailableArchiveImages(missing);
    const assertSnapshot = async () => {
      await assertCurrent(); const latest = await get(keyFor(id, account));
      if (!latest || latest.revoked || latest.account !== account.key || latest.project?.id !== id) throw fail('Site access changed during export. Its original backup was kept.', 403);
      await selectedBackup(latest, account, id, selection); await assertCurrent();
    };
    await assertSnapshot();
    const result = await archive.buildRecoveryArchive(payload, assets, { signal, onProgress, assertCurrent, siteID: id, ...(photos ? { photos, photoList } : {}) });
    await assertSnapshot(); return result;
  })));
}
/** Back up the Site as it is now: the saved online plan, or this device's
 * latest plan when it has unsent saves or is offline, with its drawings and
 * the private photos the caller collected. Read-only: nothing is saved,
 * uploaded or queued, and unsaved editor changes are not included. */
export async function exportSiteArchive(id, { signal, onProgress, photos, photoList } = {}) {
  const account = active, generation = accountGeneration;
  if (!account) throw fail('Sign in to back up this Site.', 401);
  const archive = await import('./portableRecoveryArchive.js');
  const assertCurrent = async () => {
    archive.checkArchiveSignal(signal);
    await owned(account);
    if (generation !== accountGeneration) throw fail('Your account session changed. Prepare the Site backup again.', 401);
    archive.checkArchiveSignal(signal);
  };
  await assertCurrent();
  if (!navigator.locks) throw new Error('Use an updated browser with coordinated offline storage to back up drawings.');
  const lease = (name, mode, fn) => navigator.locks.request(name, { mode, ...(signal ? { signal } : {}) }, fn);
  return lease(storageLock(account), 'shared', () => lease('avisera-site:' + keyFor(id, account), 'exclusive', () => lease('avisera-images:' + account.key, 'shared', async () => {
    await assertCurrent();
    const record = await get(keyFor(id, account));
    if (record?.revoked) throw fail('Access to this Site was revoked on this device. Reconnect and verify access before backing it up.', 403);
    if (record && (record.account !== account.key || record.project?.id !== id)) throw fail('Reopen this Site under its owning account before backing it up.', 403);
    const online = navigator.onLine !== false, unsent = !!(record?.creation || record?.pending.length);
    let source;
    if (record && (unsent || !online)) source = { doc: clone(record.doc), name: record.project.name, savedAt: record.savedAt, from: 'device', unsent: (record.creation ? 1 : 0) + record.pending.length };
    else if (online) {
      let response;
      try { response = await fetch('/api/projects/' + encodeURIComponent(id), { credentials: 'same-origin', cache: 'no-store', redirect: 'error', ...(signal ? { signal } : {}) }); }
      catch (error) { if (error.name === 'AbortError') throw error; throw fail('The Site could not be read. Check your connection and try again.'); }
      await assertCurrent();
      const body = await response.json().catch(() => null); await assertCurrent();
      if (!response.ok || body?.project?.id !== id || !body.doc || typeof body.doc !== 'object') throw fail(body?.error || 'The Site could not be read for backup.', response.status);
      source = { doc: body.doc, name: body.project.name, savedAt: body.project.updatedAt || new Date().toISOString(), from: 'online', unsent: 0 };
    } else throw fail('Connect to back up this Site, or prepare it for offline work first.');
    // Restore needs an RFC 3339 time. SQLite reports UTC as "YYYY-MM-DD HH:MM:SS" without a zone.
    const stamp = typeof source.savedAt === 'string' ? source.savedAt.trim().replace(/^(\d{4}-\d{2}-\d{2}) (\d)/, '$1T$2') : '';
    const parsed = Date.parse(/(Z|[+-]\d{2}:?\d{2})$/i.test(stamp) ? stamp : stamp + 'Z');
    source.savedAt = Number.isFinite(parsed) ? new Date(parsed).toISOString() : new Date().toISOString();
    const payload = { doc: source.doc, recovery: { name: source.name, savedAt: source.savedAt, pending: [] } };
    const snapshot = archive.recoveryArchiveSnapshot(payload);
    const { assets, missing } = await readArchiveDrawings(account, archive, snapshot, { signal, assertCurrent, onProgress, online });
    if (missing.length) throw archive.unavailableArchiveImages(missing);
    const result = await archive.buildRecoveryArchive(payload, assets, { signal, onProgress, assertCurrent, siteID: id, filenamePrefix: 'avisera-site-backup-', ...(photos ? { photos, photoList } : {}) });
    await assertCurrent();
    return { ...result, source: source.from, unsent: source.unsent, name: source.name };
  })));
}
export async function discardOfflineBackup(id, selection) {
  const account = active; await owned(account);
  return navigator.locks.request(editorLock(account), { ifAvailable: true }, async lease => {
    if (!lease) throw new Error('Close open Site editors before discarding a recovery backup.');
    return lock(keyFor(id, account), async () => {
      await owned(account);
      const record = await get(keyFor(id, account));
      const entry = await selectedBackup(record, account, id, selection);
      if (record.creation || record.pending.length) throw new Error('Upload pending changes before discarding a recovery backup.');
      if (readStorage(`draft.${account.user.id}.${id}`) != null) throw new Error('Open this Site and save or discard its recovery draft first.');
      if (entry.slot === 'current') delete record.recovered;
      else record.recoveryHistory.splice(Number(entry.slot.slice(8)), 1);
      // Images are retained here: they may still be needed by exported plan data.
      await owned(account); await put(record); notify();
    });
  });
}

// Retained uploads remain separate from the live document and immutable save
// queue. Recovery never regenerates upload identities or contacts DELETE APIs.
const referenceAccountRecords = (values, account) => values.filter(value => value?.account === account.key || typeof value?.key === 'string' && value.key.startsWith(account.key + '/'));
export async function retainedReferenceDrawings() {
  const account = active, assertCurrent = cleanupHistoryGuard(account); await owned(account); assertCurrent();
  if (!navigator.locks) throw new Error('Use an updated browser with offline storage coordination to review retained drawings.');
  return navigator.locks.request(storageLock(account), { mode: 'shared' }, async () => {
    await owned(account);
    const entries = retainedDrawingMetadata(await referenceMetadata(await db(), account.key), account.key);
    const saved = referenceAccountRecords(await access('sites', store => store.getAll()), account);
    let index, history, problem = '';
    try { index = retainedDrawingReferences(saved, browserDrafts(), account.key); history = await readHistoryCleanupReferences(await db(), account.key, assertCurrent); }
    catch (error) { problem = error.message; }
    const result = [];
    for (const descriptor of entries) {
      const reason = problem || history?.references.get(descriptor.imageID) || retainedDrawingReason(descriptor, index);
      result.push({ imageID: descriptor.imageID, projectID: descriptor.projectID,
        siteName: index?.sites.get(descriptor.projectID)?.project.name || 'Site no longer downloaded',
        filename: descriptor.filename, mime: descriptor.mime, size: descriptor.size, sha256: descriptor.sha256,
        state: descriptor.state, error: descriptor.error || '', createdAt: descriptor.createdAt,
        token: await retainedDrawingToken(account.key, descriptor), referenced: !!(index?.references.has(descriptor.imageID) || history?.references.has(descriptor.imageID)),
        reason, discardable: !reason });
    }
    await owned(account); assertCurrent();
    return result.sort((a,b) => b.createdAt.localeCompare(a.createdAt) || a.imageID.localeCompare(b.imageID));
  });
}
async function selectedDrawing(account, selection) {
  const descriptor = await access('meta', store => store.get(referenceMetadataKey(account.key, selection?.imageID)));
  return selectedRetainedDrawing(account.key, descriptor, selection);
}
export async function readRetainedReferenceDrawing(selection) {
  const account = active; await owned(account);
  if (!navigator.locks) throw new Error('Use an updated browser with offline storage coordination to export this drawing.');
  const initial = await selectedDrawing(account, selection);
  return lock(keyFor(initial.projectID, account), () => navigator.locks.request('avisera-images:' + account.key, { mode: 'shared' }, async () => {
    await owned(account);
    const descriptor = await selectedDrawing(account, selection);
    const blob = await verifiedRetainedBytes(descriptor, await access('meta', store => store.get(referenceBlobKey(account.key, descriptor.imageID))));
    const record = await get(keyFor(descriptor.projectID, account));
    await owned(account);
    // A complete immutable original is returned. The UI owns its object URL
    // and must revoke it on close/account changes; exporting changes no state.
    return { blob, filename: descriptor.filename, mime: descriptor.mime, size: descriptor.size, sha256: descriptor.sha256,
      projectID: descriptor.projectID, imageID: descriptor.imageID, siteName: typeof record?.project?.name === 'string' ? record.project.name : 'Site no longer downloaded' };
  }));
}
export async function discardRetainedReferenceDrawing(selection) {
  const account = active, assertCurrent = cleanupHistoryGuard(account); await owned(account); assertCurrent();
  if (!navigator.locks) throw new Error('This browser cannot safely coordinate drawing removal. The original was kept.');
  const initial = await selectedDrawing(account, selection);
  return navigator.locks.request(editorLock(account), { ifAvailable: true }, async editorLease => {
    if (!editorLease) throw new Error('Close open Site editors before discarding a retained drawing.');
    return navigator.locks.request(storageLock(account), () => navigator.locks.request('avisera-site:' + keyFor(initial.projectID, account), () =>
      navigator.locks.request('avisera-images:' + account.key, { ifAvailable: true }, async imageLease => {
        if (!imageLease) throw new Error('A drawing read or download is in progress. Wait for it to finish before discarding the original.');
        await owned(account);
        const descriptor = await selectedDrawing(account, selection), drafts = browserDrafts();
        const saved = referenceAccountRecords(await access('sites', store => store.getAll()), account);
        const history = await readHistoryCleanupReferences(await db(), account.key, assertCurrent);
        const reason = history.references.get(descriptor.imageID) || retainedDrawingReason(descriptor, retainedDrawingReferences(saved, drafts, account.key));
        if (reason) throw new Error(reason);
        await verifiedRetainedBytes(descriptor, await access('meta', store => store.get(referenceBlobKey(account.key, descriptor.imageID))));
        await owned(account); sameDrafts(drafts);
        const database = await db(), serialized = JSON.stringify(descriptor);
        await new Promise((resolve, reject) => {
          const tx = database.transaction(['sites','meta'], 'readwrite'), metaStore = tx.objectStore('meta');
          let current, storedAccount, records, bytes, history, remaining = 5, failure;
          const abort = error => { failure = error; try { tx.abort(); } catch {} };
          const finish = () => {
            if (--remaining) return;
            try {
              assertCurrent();
              if (active?.key !== account.key || storedAccount?.key !== account.key) throw fail('Sign in to the account that owns this retained drawing.', 401);
              if (JSON.stringify(current) !== serialized) throw new Error('This retained drawing changed in another window. Select it again from the refreshed list.');
              if (!(bytes instanceof Blob) || bytes.size !== descriptor.size || bytes.type !== descriptor.mime) throw new Error('The original drawing bytes changed. They were kept.');
              sameDrafts(drafts);
              const reason = history.references.get(descriptor.imageID) || retainedDrawingReason(descriptor, retainedDrawingReferences(referenceAccountRecords(records, account), drafts, account.key));
              if (reason) throw new Error(reason);
              metaStore.delete(referenceMetadataKey(account.key, descriptor.imageID));
              metaStore.delete(referenceBlobKey(account.key, descriptor.imageID));
              assertCurrent();
            } catch (error) { abort(error); }
          };
          const read = (request, assign) => { request.onsuccess = () => { assign(request.result); finish(); }; };
          read(metaStore.get('account'), value => { storedAccount = value; });
          read(metaStore.get(referenceMetadataKey(account.key, descriptor.imageID)), value => { current = value; });
          read(metaStore.get(referenceBlobKey(account.key, descriptor.imageID)), value => { bytes = value; });
          read(tx.objectStore('sites').getAll(), value => { records = value; });
          requestHistoryCleanupReferences(metaStore, account.key, value=>{history=value;finish();}, abort);
          tx.oncomplete = resolve; tx.onabort = tx.onerror = () => reject(failure || tx.error || new Error('The drawing could not be discarded. Its original was kept.'));
        });
        let warning = '';
        // Cache Storage is not transactional with IndexedDB. Its optional
        // duplicate is pruned after the original's successful atomic removal.
        try {
          assertCurrent();
          if (active?.key !== account.key) throw new Error('Account changed');
          const name = 'avisera-private-images-' + encodeURIComponent(account.key);
          if (globalThis.caches && await caches.has(name)) await (await caches.open(name)).delete('/api/images/' + descriptor.imageID);
        } catch { warning = 'The original drawing was discarded. A cached preview may remain; use Remove unused images to clear it.'; }
        try { notify(); } catch {}
        return { removed: true, bytes: descriptor.size, ...(warning ? { warning } : {}) };
      })
    ));
  });
}

export async function offlineRequest(method, url, body, opts, send) {
  transport = send;
  if (url === '/api/auth/me' && method === 'GET' || url === '/api/auth/login' && method === 'POST') {
    try {
      const result = await send(method, url, body, opts);
      // A signed-out reply ({ user: null }) is an answer, not an outage: clear
      // the active account as logout does, so the app shows sign-in.
      if (!result?.user) { active = null; try { await setMeta(null); } catch {} notifyAccount(); return result; }
      active = { key: accountKey(result.user), user: result.user };
      // Storage failure must not prevent ordinary online access.
      try { await setMeta(active); } catch {}
      notifyAccount();
      return result;
    } catch (e) {
      if (unavailable(e) && url === '/api/auth/me') {
        try { const saved = await cachedAccount(); if (saved) { active = saved; return { user: saved.user, offline: true }; } } catch {}
      } else { active = null; try { await setMeta(null); } catch {} notifyAccount(); }
      throw e;
    }
  }
  if (url === '/api/auth/logout') {
    active = null; try { await setMeta(null); } catch {} notifyAccount();
    return send(method, url, body, opts);
  }
  if (Object.hasOwn(opts, 'historyState') || Object.hasOwn(opts, 'historyAnchor') || Object.hasOwn(opts, 'sourceState') || opts.localHistoryCommitOnly) {
    if (!opts.localHistoryCommitOnly || !Object.hasOwn(opts, 'historyState') || !Object.hasOwn(opts, 'historyAnchor') || !active || !navigator.locks || typeof opts.assertCurrent !== 'function' || (!!opts.referenceUpload !== Object.hasOwn(opts, 'sourceState')) || method !== 'POST' || !/^\/api\/projects\/[^/]+\/versions$/.test(url)) throw fail('Reopen the prepared signed-in Site before saving retained history.', 409);
    const captured = captureHistorySave(body, opts.historyState, opts.historyAnchor, opts.referenceUpload ? { attempt: opts.referenceUpload, sourceState: opts.sourceState } : undefined), account = active, id = url.split('/')[3], offlineVersion = opts.offlineVersion;
    const assertCurrent = await historySession(account, opts.assertCurrent);
    return lock(keyFor(id, account), () => navigator.locks.request('avisera-images:' + account.key, async () => {
      const receipt = await acceptHistorySave(await db(), { account: account.key, projectID: id, ...captured, offlineVersion }, { assertCurrent });
      try { notify(); } catch {} return receipt;
    }));
  }
  if (opts.localCommitOnly || opts.referenceUpload) {
    if (!opts.localCommitOnly || !opts.referenceUpload || !active || !navigator.locks || typeof opts.assertCurrent !== 'function' || method !== 'POST' || !/^\/api\/projects\/[^/]+\/versions$/.test(url)) throw fail('Reopen the signed-in Site before saving this drawing on the device.', 409);
  }
  if (!active || !navigator.locks) return send(method, url, body, opts);
  const account = active;
  if (method === 'POST' && url === '/api/projects' && opts.allowOfflineCreation && await offlineCreationReady()) {
    return createDeviceSite(body, send, account);
  }
  if (method === 'POST' && url === '/api/projects' && opts.allowOfflineCreation && navigator.onLine === false) throw fail('Connect and choose Prepare for offline work before starting Sites on this device.');
  if (method === 'GET' && url.startsWith('/api/projects') && /^\/api\/projects(?:\?.*)?$/.test(url)) {
    const decorate = (project, record) => ({ ...project, availableOffline: !!record?.ready, pendingCreation: !!record?.creation, pendingUpload: !!(record?.creation || record?.pending.length) });
    try {
      const result = await send(method, url, body, opts), cached = await records(account).catch(() => []);
      const ids = new Set(result.projects.map(p => p.id));
      return { ...result, projects: [...result.projects.map(p => decorate(p, cached.find(r => r.project.id === p.id))),
        ...cached.filter(r => (r.creation || r.pending.length) && !ids.has(r.project.id) && !url.includes('archived=1')).map(r => decorate(r.project, r))] };
    }
    // Device copies are only part of the account's list, so the caller must be
    // told the server failed rather than render them as the complete set.
    catch (e) { if (!unavailable(e)) throw e; await owned(account); return { projects: (await records(account)).filter(r => !r.revoked && !r.project.archived && !url.includes('archived=1')).map(r => decorate(r.project, r)), deviceOnly: true, error: e.message }; }
  }
  const read = method === 'GET' && url.match(/^\/api\/projects\/([^/]+)$/);
  const save = method === 'POST' && url.match(/^\/api\/projects\/([^/]+)\/versions$/);
  const catalogue = method === 'POST' && url === '/api/catalogues/select' && body?.source?.scope === 'site';
  if (catalogue) {
    try { return await send(method, url, body, opts); }
    catch (e) {
      const record = await get(keyFor(body.source.projectId));
      if (!unavailable(e) || !record || record.revoked) throw e;
      return { models: [...new Map([record.doc, ...(record.doc.floors || []).map(f => f.plan)].flatMap(f => f.models || []).map(m => [m.id, m])).values()] };
    }
  }
  if (!read && !save) return send(method, url, body, opts);
  const id = (read || save)[1], key = keyFor(id);
  let preparedReference;
  if (opts.referenceUpload) {
    const readiness = await offlineReferenceAvailability(id);
    if (!readiness.available) throw fail(readiness.reason, 409);
    preparedReference = await prepareDurableReference(opts.referenceUpload, account.key, id, opts.assertCurrent);
  }
  return lock(key, async () => {
    let record;
    try { record = await get(key); await owned(account); }
    catch (e) {
      // Reads may continue online. Saves must never bypass an unreadable queue.
      if (read && !e.status) return send(method, url, body, opts);
      throw e;
    }
    if (!record) {
      if (preparedReference) throw fail('This Site download was removed. Prepare it again before importing drawings.', 409);
      if (save && opts.offlineVersion != null) throw fail('This offline download was removed. Reopen the online Site before saving; your unsaved edits are retained.', 409);
      return send(method, url, body, opts);
    }
    if (read) {
      if (!record.creation && !record.pending.length) {
        try {
          const latest = await send(method, url, body, opts);
          if (latest.project.rev !== record.project.rev) record.generation++;
          Object.assign(record, latest, { revoked: false }); await put(record);
        } catch (e) {
          if (!unavailable(e)) { if ([401,403,404].includes(e.status)) { record.revoked = true; await put(record); } throw e; }
          if (record.revoked) throw fail('Reconnect and sign in to reopen this Site.', 401);
        }
      }
      return { project: record.project, doc: record.doc, offlineVersion: record.generation };
    }
    if (preparedReference) {
      const worker = navigator.serviceWorker?.controller;
      if (!worker || (await workerMessage(worker, { action: 'editorStorage' }, undefined, 10000)).localReferenceUploads !== 1) throw fail('Update the offline editor before saving new drawings on this device.', 409);
      const assertCurrent = () => {
        if (active?.key !== account.key || navigator.serviceWorker.controller !== worker) throw fail('The account or offline editor changed. Reopen this drawing review.', 409);
        opts.assertCurrent();
      };
      const receipt = await commitDurableReference(await db(), { account: account.key, projectID: id, body, offlineVersion: opts.offlineVersion, prepared: preparedReference, assertCurrent });
      // This receipt is final once the transaction commits. No network request
      // or fallible postcommit storage read may turn it into a false failure.
      try { notify(); } catch {} return receipt;
    }
    if (opts.offlineVersion !== record.generation) throw fail('This Site changed in another window. Your unsaved edits are retained; reopen the saved Site before continuing.', 409);
    record.doc = clone(body.doc); record.generation++;
    record.pending.push({ doc: clone(body.doc), note: body.note || '', mutationId: crypto.randomUUID(), expectedRev: null });
    record.savedAt = new Date().toISOString(); await put(record); if (record.createdOnDevice) reportDeviceSite(record); notify();
    record = await drain(record, send, account);
    return { rev: record.project.rev, offline: !!(record.creation || record.pending.length), offlineVersion: record.generation };
  });
}

async function workerMessage(worker, message, progress, timeout = 20 * 60 * 1000) {
  return new Promise((resolve, reject) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => { channel.port1.close(); reject(new Error('The offline editor did not respond in time. Reopen Planner and try again.')); }, timeout);
    channel.port1.onmessage = ({ data }) => {
      if (data.progress) { progress?.(data.progress); return; }
      clearTimeout(timer); channel.port1.close(); data.error ? reject(new Error(data.error)) : resolve(data);
    };
    worker.postMessage(message, [channel.port2]);
  });
}
export async function prepareOfflineEditor(progress) {
  if (!navigator.serviceWorker || !navigator.locks) throw new Error('Offline editing needs an updated Avisera app or a browser with offline storage support.');
  const account = active;
  await owned(account); await identify(transport);
  progress?.('Downloading editor…');
  const registration = await navigator.serviceWorker.register('/offline-worker.js', { scope: '/' });
  const installing = registration.installing || registration.waiting;
  if (installing && installing.state !== 'activated') await new Promise((resolve, reject) => {
    const finish = () => {
      if (installing.state === 'activated' || installing.state === 'redundant') {
        clearTimeout(timer); installing.removeEventListener('statechange', finish);
        installing.state === 'activated' ? resolve() : reject(new Error('Offline download was interrupted. Reconnect and try again.'));
      }
    };
    const timer = setTimeout(() => { installing.removeEventListener('statechange', finish); reject(new Error('Offline download timed out. Reconnect and try again.')); }, 300000);
    installing.addEventListener('statechange', finish); finish();
  });
  const ready = registration;
  if (!ready.active) throw new Error('The offline editor did not start. Reconnect and try again.');
  await workerMessage(ready.active, { action: 'prepare' }, progress);
  await owned(account); await access('meta', s => s.put(true, 'editor:' + account.key), true);
  try {
    const capability = await transport('GET', '/api/image-uploads/capabilities', undefined, {});
    validateReferenceUploadCapabilities(capability, { size: 0 }); await owned(account);
    await access('meta', s => s.put(capability, referenceCapabilityKey(account.key)), true);
  } catch (error) {
    await owned(account);
    // A failed update must not falsely advertise a supported upload server.
    await access('meta', s => s.delete(referenceCapabilityKey(account.key)), true);
  }
  try { await navigator.storage?.persist?.(); } catch {}
  notify(); return ready;
}
export async function prepareOfflineSite(id, progress) {
  const account = active, key = keyFor(id);
  const registration = await prepareOfflineEditor(progress);
  return lock(key, async () => {
    await owned(account);
    const current = await get(key);
    const latest = current?.creation || current?.pending.length ? { project: current.project, doc: current.doc } : await transport('GET', `/api/projects/${id}`, undefined, {});
    const imageIDs = new Set();
    const scan = object => { if (!object || typeof object !== 'object') return; for (const [k,v] of Object.entries(object)) { if (['imageId','photoId','testImageId'].includes(k) && typeof v === 'string') imageIDs.add(v); else if (typeof v === 'object') scan(v); } };
    scan(latest.doc);
    await workerMessage(registration.active, { action: 'images', account: account.key, ids: [...imageIDs] }, progress);
    const record = { ...current, key, account: account.key, ...latest, generation: current?.generation || 0, pending: current?.pending || [], ready: true, revoked: false };
    await put(record); notify();
    reportDeviceSite(record);
    return record;
  });
}

if (typeof window !== 'undefined') {
  navigator.serviceWorker?.addEventListener('message', event => {
    if (event.data?.action === 'avisera-history-image-context' && event.ports[0]) {
      event.ports[0].postMessage(historyImageContexts.read()); return;
    }
    if (event.data?.action === 'avisera-reference-account' && event.ports[0]) {
      event.ports[0].postMessage({ account: active?.key || null }); return;
    }
    if (event.data?.action !== 'avisera-editor-assets' || !event.ports[0]) return;
    const urls = [...document.querySelectorAll('script[type="module"][src],link[rel="stylesheet"][href]')].map(node => new URL(node.src || node.href, location.href));
    const assets = urls.filter(url => url.origin === location.origin).map(url => url.pathname);
    event.ports[0].postMessage({ assets });
  });
  const sync = () => syncOfflineSites().catch(() => {});
  window.addEventListener('online', sync); window.addEventListener('focus', sync);
  setInterval(() => { if (!document.hidden) sync(); }, 30000);
}
